import React, { FC, useEffect, useState } from 'react';
import ModuleSelectFormGroup from './ModuleSelectFormGroup';
import SubsourceSelectFormGroup from './SubsourceSelectFormGroup';
import SupersourceSelectFormGroup from './SupersourceSelectFormGroup';
import { FileInfo, ModuleInstance, Supersource } from './types';
import TextareaAutosize from 'react-autosize-textarea';
import Select from 'react-select';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

interface ModuleInstanceRowProps {
  formIndex: string;
  studentId: string;
  moduleInstance: ModuleInstance;
  supersources: Supersource[];
  includeModuleIds: number[];
  userFiles: FileInfo[];
  canSlideUp: boolean;
  canSlideDown: boolean;
  onChangeSupersourceId: (id: number) => void;
  onChangeSubsourceId: (id: number) => void;
  onChangeModuleId: (id: number) => void;
  onSlideUp: () => void;
  onSlideDown: () => void;
  onDelete: () => void;
  onAddCustomFile: () => void;
  onUpdateCustomContent: (content: string) => void;
  onDeleteCustomFile: (index: number) => void;
  onUpdateCustomFile: (index: number, customFileId: number) => void;
  overrideFormName: string;
}

const ModuleInstanceRow: FC<ModuleInstanceRowProps> = ({
  formIndex,
  studentId,
  moduleInstance,
  supersources,
  includeModuleIds,
  userFiles,
  canSlideUp,
  canSlideDown,
  onChangeSupersourceId,
  onChangeSubsourceId,
  onChangeModuleId,
  onSlideUp,
  onSlideDown,
  onDelete,
  onAddCustomFile,
  onUpdateCustomContent,
  onDeleteCustomFile,
  onUpdateCustomFile,
    overrideFormName,
}) => {
  const hasCustomFields = !!(moduleInstance.customFileIds.length || moduleInstance.customContent);
  const [isShowingCustomFields, setIsShowingCustomFields] = useState(hasCustomFields);
  const [isHighlighting, setIsHighlighting] = useState(false);
  let formName = 'module_instances';
  if (overrideFormName !== undefined) {
    formName = overrideFormName;
  }
  // always set highlighting = false on next tick so the effect pulses
  useEffect(() => {
    if (isHighlighting) setIsHighlighting(false);
  }, [isHighlighting]);

  const fileOpts = userFiles.map(file => ({
    label: `${file.title} (${file.file})`,
    value: file.id,
  }));

  return (
    <CSSTransition
      classNames={{
        exit: 'module-instance-highlight-exit',
        exitActive: 'module-instance-highlight-exit-active',
        exitDone: 'module-instance-highlight-exit-done',
      }}
      timeout={0}
      in={isHighlighting}
    >
      <div
        className="row border-top module-instance"
        data-form-index={formIndex}
      >
        <input type="hidden" name={`${formName}-${formIndex}-id`} value={moduleInstance.id} />
        <div className="col-12">
          <div className="row">
            <div className="col-md-3">
            <SupersourceSelectFormGroup
                formIndex={formIndex}
                supersources={supersources}
                selectedSupersourceId={moduleInstance.supersourceId}
                onChange={onChangeSupersourceId}
                overrideFormName={overrideFormName}
              />
            </div>
            <div className="col-md-3">
              <SubsourceSelectFormGroup
                formIndex={formIndex}
                supersourceId={moduleInstance.supersourceId}
                selectedSubsourceId={moduleInstance.subsourceId}
                onChange={onChangeSubsourceId}
                overrideFormName={overrideFormName}
              />
            </div>
            <div className="col-md-4 js_module_select">
            <ModuleSelectFormGroup
                formIndex={formIndex}
                subsourceId={moduleInstance.subsourceId}
                studentId={studentId}
                selectedModuleId={moduleInstance.moduleId}
                onChange={onChangeModuleId}
                includeModuleIds={includeModuleIds}
                overrideFormName={overrideFormName}
              />
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label>Actions</label>
                <div>
                  <button
                    onClick={onDelete}
                    className="btn btn-danger"
                    type="button"
                  >
                    <i className="fa fa-minus-circle"></i>
                  </button>
                  <div className="lesson-module-move-arrows pull-right">
                    {canSlideUp && <i
                      className="fa fa-arrow-up lesson-assign"
                      title="Move up"
                      onClick={() => {
                        setIsHighlighting(true);
                        onSlideUp();
                      }}
                    />}
                    {canSlideDown && <i
                      className="fa fa-arrow-down lesson-assign"
                      title="Move down"
                      onClick={() => {
                        setIsHighlighting(true);
                        onSlideDown();
                      }}
                    />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <a
                href="#"
                style={{ fontSize: '0.75em' }}
                onClick={evt => {
                  evt.preventDefault();
                  setIsShowingCustomFields(!isShowingCustomFields);
                }}
              >
                {isShowingCustomFields ? '[-] Hide custom fields' : '[+] Show custom fields'}
              </a>
            </div>
          </div>
          <div className={classNames('row', { hidden: !isShowingCustomFields })}>
            <div className="form-group col-md-8">
              <label htmlFor={`${formName}-${formIndex}-custom_content`}>
                CUSTOM TEXT
              </label>
              <TextareaAutosize
                className="form-control short-textarea"
                onChange={(evt) => onUpdateCustomContent((evt.target as any).value)}
                name={`${formName}-${formIndex}-custom_content`}
                value={moduleInstance.customContent}
                rows={2}
                maxRows={10}
              />
            </div>
          </div>
          <div className={classNames('form-group', { hidden: !isShowingCustomFields })}>
            <label>
              CUSTOM FILES
            </label>
            {moduleInstance.customFileIds.map((fileId, fileIndex) => (
              <div className="row" key={fileIndex}>
                <div className="col-10">
                  <Select
                    options={fileOpts}
                    value={fileOpts.find(({ value }) => value === fileId) ?? null}
                    onChange={({ value }) => onUpdateCustomFile(fileIndex, value)}
                    name={`${formName}-${formIndex}-module_instance_custom_files-${fileIndex}-custom_file`}
                  />
                </div>
                <div className="col-2">
                  <button
                    onClick={() => onDeleteCustomFile(fileIndex)}
                    className="btn btn-danger btn-outline"
                    type="button"
                  >
                    <i className="fa fa-minus-circle"></i>
                  </button>
                </div>
              </div>
            ))}
            <div>
              <button
                onClick={onAddCustomFile}
                className="btn btn-outline"
                type="button"
              >
                <i className="fa fa-plus"></i> Add custom file
              </button>
            </div>
          </div>
          <input
            name={`${formName}-${formIndex}-position`}
            type="hidden"
            value={moduleInstance.position}
          />
        </div>
      </div>
    </CSSTransition>
  );
};

export default ModuleInstanceRow;
